<template>
  <VcModalContainer showDialog
                    headerRightButtonIcon="icon-Minimize"
                    flavor="input"
                    @onCloseButtonClicked="closeDialog"
                    :dataQa="dataQa"
                    :showFooter="false"
                    :size="size"
                    :useOffset="useOffset"
                    :showBackButton="false">
    <template #header>
      <VcModalHeader class="message-dialog__header" flavor="input" :closeButtonIcon="newMessageMode ? 'icon-close' : 'icon-Minimize'"
                     @onCloseButtonClicked="closeDialog"
                     :title="$t('conversation.new_message.title')"
                     :closeButton="true"
                     :showBackButton="false"/>
    </template>
    <template #content>
      <div class="message-dialog">
        <div v-if="alertProp.type">
          <VcAlert v-bind="alertProp"/>
        </div>
        <div class="message-dialog__from" v-if="newMessageMode && shouldDisplayStaffSelect">
          <span class="message-dialog__from--label">{{$t('conversation.new_message.from')}}</span>
          <SimpleStaffPicker data-qa="new-message-staff-selection"
                             :selectedStaff="selectedStaff"
                             :staffs="activeStaffMembers"
                             @staffSelected="staffSelected" />
        </div>
        <div class="message-dialog__to">
          <span class="message-dialog__to--label">{{$t('conversation.new_message.to')}}</span>
          <VcChipList v-if="chips.length > 1"
                      dataQa="new-message-chip-list"
                      :items="chips"
                      :lines="1"
                      :moreText="$t('conversation.new_message.more_text', {hidden_matters:hiddenMatterChips})"
                      @onHiddenChipsChange="num => hiddenMatterChips = num"/>
          <v-tooltip v-else top>
            <template #activator="{ on, attrs }">
              <span v-on="on" v-bind="attrs">
                <VcChip v-bind="chips[0]"/>
              </span>
            </template>
            <span>{{contactHint}}</span>
          </v-tooltip>
        </div>
        <div v-if="showSubject" class="message-dialog__subject">
          {{$t('conversation.new_message.subject')}}
          <input :data-testid="`${dataQa}-subject`"
                 :data-qa="`${dataQa}-subject`"
                 ref="subjectInput"
                 class="message-dialog__subject--input"
                 type="text"
                 v-model="subject"
                 @input="$emit('subject', subject.trim())"/>
        </div>
        <slot name="content"/>
      </div>
    </template>
  </VcModalContainer>
</template>

<script>
import RouteGuardMixin from "@/modules/common/mixins/RouteGuardMixin";
import {EMAIL_CHANNEL_UID} from "@/modules/account/constants/channels.js";
import {mapGetters} from "vuex";
import SimpleStaffPicker from "@/modules/inbox/views/NewMessage/SimpleStaffPicker.vue";
export default {
  name: 'MessageDialog',
  components: {SimpleStaffPicker},
  mixins: [RouteGuardMixin],
  props: {
    dataQa: {
      type: String,
      default: 'message-dialog'
    },
    chips: {
      type: Array,
      required: true
    },
    channel: {
      type: String,
      required: true
    },
    subjectFromPage: {
      type: String,
      default: ''
    },
    contactHint: {
      type: String,
      default: ''
    },
    useOffset: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'lg'
    },
    newMessageMode: {
      type: Boolean,
      default: false
    },
    alertProp: {
      type: Object,
      default: () => ({})
    },
  },
  data() {
    return {
      subject: '',
      message: '',
      hiddenMatterChips: 0,
      RGInitOnStart: true,
    }
  },
  created() {
    this.subject = this.subjectFromPage;
    // Force VcChipList to update when dialog opening animation finish
    setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
  },
  computed: {
    ...mapGetters('accountModule', ['staff', 'staffMembers', 'isMultipleStaffs']),
    activeStaffMembers() {
      return this.staffMembers.filter(staff => staff.active);
    },
    showSubject() {
      return this.channel === EMAIL_CHANNEL_UID;
    },
    shouldDisplayStaffSelect() {
      return this.isMultipleStaffs && ['admin', 'manager', 'collaborator'].indexOf(this.staff.role) !== -1;
    },
    selectedStaff() {
      return this.staff;
    }
  },
  methods: {
    closeDialog() {
      this.$emit('onCloseButtonClicked');
    },
    staffSelected(staff) {
      if(this.showSubject) {
        this.$refs.subjectInput?.focus()
      } else {
        window.tinyMCE?.activeEditor?.focus()
      }
      this.$emit('staffSelected', staff);
    }
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/abstract/_mixins.scss";
.message-dialog {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__header {
    border-bottom: var(--border-frame);
  }

  &__to ::v-deep {
    .more-tooltip {
      max-width: 230px;
    }
  }

  &__to, &__from, &__subject {
    border-bottom: var(--border-frame);
    padding-block: 6px;
    margin-block-start: 9px;
    color: var(--gray-darken-3);
    margin-inline: var(--size-value4);
    font-size: var(--font-size-small);
    display: flex;
    align-items: center;

    &--label {
      margin-inline-end: var(--size-value3);
    }
  }

  &__from ::v-deep {
    .v-input__slot {
      padding: 0;
    }
  }

  &__subject {
    display: flex;
    margin-block-end: var(--size-value3);

    &--input {
      color: var(--gray-darken-5);
      font-weight: var(--font-weight-medium2);
      flex: 1 1 auto;
      outline: 0;
      padding-inline-start: var(--size-value3);
    }
  }
}
</style>
